import {
  Notebook,
  ShieldCheck,
  Gauge,
  CheckCircle,
  GitBranch,
  Stack,
  Clock,
  Sparkle,
  Robot,
} from "@phosphor-icons/react";
import { XrefText } from "../lib/xrefText";

/**
 * RoadmapListRow — single row in the left-pane flat list of unified roadmap
 * items (Phase 12E master-detail layout, cycle-5c+25-fix8).
 *
 * Compact card showing type-badge + tier + agent badges + effort + title +
 * one-line summary + last-decision marker. Clicking emits `onSelect(item.id)`
 * which the page consumes to populate the right-pane detail view.
 */

const TYPE_BADGE = {
  promise:    { icon: Stack,        label: "Promise",    bg: "hsl(var(--primary) / 0.10)",        fg: "hsl(var(--primary))",        border: "hsl(var(--primary) / 0.40)" },
  workbook:   { icon: Notebook,     label: "Workbook",   bg: "hsl(var(--status-warning) / 0.10)", fg: "hsl(var(--status-warning))", border: "hsl(var(--status-warning) / 0.40)" },
  safety_net: { icon: ShieldCheck,  label: "Safety-net", bg: "hsl(var(--status-critical) / 0.10)",fg: "hsl(var(--status-critical))",border: "hsl(var(--status-critical) / 0.40)" },
  feature:    { icon: GitBranch,    label: "Feature",    bg: "hsl(var(--muted) / 0.50)",          fg: "hsl(var(--foreground))",     border: "hsl(var(--border))" },
  activity:   { icon: Gauge,        label: "Activity",   bg: "hsl(var(--muted) / 0.40)",          fg: "hsl(var(--muted-foreground))", border: "hsl(var(--border))" },
  ledger:     { icon: CheckCircle,  label: "Decided",    bg: "hsl(var(--status-success) / 0.10)", fg: "hsl(var(--status-success))", border: "hsl(var(--status-success) / 0.40)" },
};

const TIER_LABEL = {
  P0: "P0", P1: "P1", P2: "P2", P3: "P3", P4: "P4", P5: "P5",
  current: "Now", next: "Next", future: "Later",
};

const EFFORT_SHORT = {
  "quick-win": "≤5m",
  "short-focus": "≤30m",
  "long-focus": "1-2h",
  "challenging": ">2h",
};

const WAITING_LABEL = {
  owner: "You",
  agent: "Agent",
  both: "Both",
  done: "Done",
};

// cycle-5c+25-fix20 (Phase 14 / Batch 3) — tiered freshness dot palette.
// Owner Q3=(d) ratification: green <7d, amber 7-30d, red >30d, grey never.
const FRESHNESS_DOT = {
  fresh: { color: "hsl(var(--status-success))", label: "Fresh" },
  aging: { color: "hsl(var(--status-warning))", label: "Aging" },
  stale: { color: "hsl(var(--status-critical))", label: "Stale" },
  never: { color: "hsl(var(--muted-foreground))", label: "Never touched" },
};

function freshnessTooltip(item) {
  const tier = item.freshness_tier || "never";
  const label = FRESHNESS_DOT[tier]?.label || "Unknown";
  const days = item.days_since_touch;
  if (!Number.isFinite(days)) return `${label} — never worked or touched`;
  const ago = days === 0 ? "today" : days === 1 ? "1 day ago" : `${days} days ago`;
  return `${label} — last touched ${ago}`;
}

function Badge({ children, color, bg, border, mono = true, testId }) {
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-md border px-1.5 py-0.5 ${mono ? "text-mono" : ""}`}
      style={{
        fontSize: "0.6rem",
        fontWeight: 600,
        color,
        background: bg,
        borderColor: border,
      }}
      data-testid={testId}
    >
      {children}
    </span>
  );
}

export default function RoadmapListRow({ item, isSelected, onSelect }) {
  const TypeIcon = TYPE_BADGE[item.type]?.icon || Stack;
  const typeStyle = TYPE_BADGE[item.type] || TYPE_BADGE.activity;
  const tierLabel = TIER_LABEL[item.tier] || null;
  const effortShort = EFFORT_SHORT[item.effort_label] || null;
  const waitingLabel = WAITING_LABEL[item.waiting_on] || null;
  const isMpRec = item.raw?.is_mp_rec || item.raw?.mp_rec_letter || item.mp_rec_rank < 50;

  return (
    <button
      type="button"
      onClick={() => onSelect(item.id)}
      className="w-full text-left rounded-md border p-2 sm:p-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      style={{
        background: isSelected ? "hsl(var(--primary) / 0.06)" : "hsl(var(--background))",
        borderColor: isSelected ? "hsl(var(--primary) / 0.6)" : "hsl(var(--border))",
      }}
      data-testid={`roadmap-list-row-${item.id}`}
      aria-pressed={isSelected}
    >
      {/* cycle-5c+25-fix22: tightened gap + smaller font for badge row at <sm. */}
      <div className="flex flex-wrap items-center gap-1 sm:gap-1.5 mb-1 sm:mb-1.5">
        {/* cycle-5c+25-fix20 (Phase 14 / Batch 3) — tiered freshness dot. */}
        {(() => {
          const tier = item.freshness_tier || "never";
          const dot = FRESHNESS_DOT[tier] || FRESHNESS_DOT.never;
          return (
            <span
              className="inline-block h-2 w-2 rounded-full shrink-0"
              style={{ background: dot.color }}
              title={freshnessTooltip(item)}
              aria-label={freshnessTooltip(item)}
              data-testid={`row-freshness-${tier}`}
            />
          );
        })()}
        {item.ref && (
          <Badge
            color="hsl(var(--primary))"
            bg="hsl(var(--primary) / 0.08)"
            border="hsl(var(--primary) / 0.4)"
            testId={`row-ref-${item.ref}`}
          >
            {item.ref}
          </Badge>
        )}
        {item.tone_check_pinned && (
          <Badge
            color="hsl(var(--background))"
            bg="hsl(var(--primary))"
            border="hsl(var(--primary))"
            testId={`row-mp-next-pin-${item.id}`}
          >
            <Sparkle size={9} weight="fill" />
            MP NEXT
          </Badge>
        )}
        <Badge
          color={typeStyle.fg}
          bg={typeStyle.bg}
          border={typeStyle.border}
          testId={`row-type-${item.type}`}
        >
          <TypeIcon size={9} weight="bold" />
          {typeStyle.label}
        </Badge>
        {tierLabel && (
          <Badge
            color="hsl(var(--foreground))"
            bg="hsl(var(--muted) / 0.5)"
            border="hsl(var(--border))"
          >
            {tierLabel}
          </Badge>
        )}
        {/* cycle-5c+25-fix22: hide agent badges on <sm to keep row compact;
            the type+tier+waiting badges carry more decision-relevant info. */}
        {item.agents.map((ag) => (
          <span key={ag} className="hidden sm:inline-flex">
            <Badge
              color="hsl(var(--muted-foreground))"
              bg="hsl(var(--background))"
              border="hsl(var(--border))"
            >
              <Robot size={9} />
              {ag.toUpperCase()}
            </Badge>
          </span>
        ))}
        {effortShort && (
          <span className="hidden sm:inline-flex">
            <Badge
              color="hsl(var(--muted-foreground))"
              bg="hsl(var(--background))"
              border="hsl(var(--border))"
            >
              <Clock size={9} />
              {effortShort}
            </Badge>
          </span>
        )}
        {waitingLabel && (
          <Badge
            color={item.waiting_on === "owner" ? "hsl(var(--status-warning))" : "hsl(var(--muted-foreground))"}
            bg={item.waiting_on === "owner" ? "hsl(var(--status-warning) / 0.1)" : "hsl(var(--background))"}
            border={item.waiting_on === "owner" ? "hsl(var(--status-warning) / 0.4)" : "hsl(var(--border))"}
          >
            ⏳ {waitingLabel}
          </Badge>
        )}
        {isMpRec && (
          <span className="hidden sm:inline-flex">
            <Badge
              color="hsl(var(--primary))"
              bg="hsl(var(--primary) / 0.10)"
              border="hsl(var(--primary) / 0.4)"
            >
              <Sparkle size={9} weight="fill" />
              MP rec
            </Badge>
          </span>
        )}
      </div>
      <h3
        className="text-sm font-semibold leading-snug"
        style={{ color: "hsl(var(--foreground))" }}
      >
        {item.title}
      </h3>
      {item.summary && (
        <p
          className="text-xs mt-1 leading-relaxed line-clamp-2"
          style={{ color: "hsl(var(--muted-foreground))" }}
        >
          <XrefText text={item.summary} />
        </p>
      )}
      {item.last_decision && (
        <div
          className="mt-1.5 text-mono"
          style={{ fontSize: "0.6rem", color: "hsl(var(--primary))" }}
        >
          Last: {(item.last_decision.answer_summary || item.last_decision.choice || "").slice(0, 50)}
          {" · "}
          {item.last_decision.submitted_at}
        </div>
      )}
    </button>
  );
}
